import { useState } from 'react'
import { Permission, notEqual, alphabeticalSort } from 'shared'
import { errorHandler } from 'hooks/errorHandler'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { CIMSModal } from 'components/CIMSModal'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import CIMSTable from 'components/CIMSTable'
import EditHeadersFormList from '../../../../../EditHeadersFormList'
import { EditHeadersFormListValues } from '../../../../../EditHeadersFormList/types'
import { RPTPayItemConnectionTableProps } from './types'
import { useSummaryContext } from '../../../../SummaryContext'

/**
 * RPTPayItemConnection implementations should return this table in their own
 * component
 *
 * @example
 *
 * const GoogleSheetTable: React.FC<GoogleSheetTableProps> = (props) => {
 *  const props = { ... }
 *  return <RPTPayItemConnectionTable {...props} />
 * }
 */
function RPTPayItemConnectionTable<T extends object>({
  additionalCustomHeaderActions,
  columns,
  connectionName,
  onEdit,
  onSync,
  records,
  refetch,
  rowKey,
  headers = [],
  loading,
}: RPTPayItemConnectionTableProps<T>) {
  const [syncing, setSyncing] = useState(false)
  const [editing, setEditing] = useState(false)
  const [visible, toggle] = useCIMSModal()
  const { selected } = useSummaryContext()
  if (!selected || selected.__typename !== 'PayItemConnection') return null
  return (
    <CIMSTable
      records={records}
      rowKey={rowKey}
      columns={columns}
      loading={loading}
      actions={{
        header: {
          onReload: refetch,
          custom: [
            {
              permission: Permission.EditPayItemConnection,
              text: 'Edit Headers',
              loading: loading,
              onClick: toggle,
            },
            {
              permission: Permission.SyncPayItemConnection,
              text: 'Sync',
              onClick: async () => {
                setSyncing(true)
                simpleSuccessNotification('Sync requested!')
                onSync(selected.id).finally(() => {
                  setSyncing(false)
                  refetch()
                })
              },
              loading: loading || syncing,
            },
            ...(additionalCustomHeaderActions ?? []),
          ],
        },
      }}
    >
      <CIMSModal
        title={`Edit ${
          selected.name ? selected.name : connectionName ?? 'Connection'
        } Headers`}
        visible={visible}
        onCancel={() => toggle(null)}
        onOk={async (values: EditHeadersFormListValues) => {
          if (!values.headers)
            return errorHandler('Something went wrong!', [
              "EditHeadersFormListValues didn't have a headers property",
            ])
          const newHeaders = values.headers.map(({ id, visible }, order) => ({
            id,
            order,
            visible,
          }))
          if (
            notEqual(
              headers.map(({ id, order, visible }) => ({
                id,
                order,
                visible,
              })),
              newHeaders,
              { compareFn: alphabeticalSort('order') }
            )
          ) {
            setEditing(true)
            await onEdit({ connectionId: selected.id, headers: newHeaders })
            setEditing(false)
            refetch()
            return toggle(null)
          }
          return toggle(null)
        }}
        loading={editing}
        narrow
      >
        <EditHeadersFormList headers={headers} />
      </CIMSModal>
    </CIMSTable>
  )
}

export default RPTPayItemConnectionTable
